import { useEffect, useState } from 'react';
import { detectVisitorPlatform, type VisitorPlatform } from './downloads';

// One platform's entry in the desktop manifest's `platforms` map. The map is
// keyed by target triple family ("windows-x86_64", "macos-aarch64",
// "macos-x86_64"); the two macOS keys point at the same universal bundle.
export interface PlatformBuild {
  download_url: string;
  bundle_name: string;
  sha256: string;
  signature: string;
  size: number;
}

// Release metadata shared by the desktop and Android manifests. Both are
// written to R2 by their release pipeline and read back through a Pages
// Function, so the site never depends on the GitHub API being up or the repo
// staying at one address.
export interface UpdateManifest {
  version: string;
  download_url: string;
  bundle_name: string;
  sha256: string;
  size: number;
  notes: string;
  min_supported: string;
  published_at: string;
  // Desktop manifest only. The flat fields above describe the legacy portable
  // Windows bundle (every installed portable client reads them), not the
  // installer the site offers; other desktop OSes live here.
  platforms?: Record<string, PlatformBuild>;
}

export type ManifestStatus = 'loading' | 'ready' | 'unpublished';

export interface ManifestState {
  manifest: UpdateManifest | null;
  status: ManifestStatus;
}

/**
 * Reads one platform's release manifest.
 *
 * A failed fetch is not treated as a page error. The desktop download is served
 * straight from R2 and works whether or not its manifest resolves, and the
 * Android manifest is legitimately absent until the first beta build ships, so
 * callers get a plain `unpublished` status to render however suits them.
 */
export function useReleaseManifest(endpoint: string): ManifestState {
  const [manifest, setManifest] = useState<UpdateManifest | null>(null);
  const [status, setStatus] = useState<ManifestStatus>('loading');

  useEffect(() => {
    let active = true;
    fetch(endpoint)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`manifest_${r.status}`))))
      .then(data => {
        if (!active) return;
        setManifest(data as UpdateManifest);
        setStatus('ready');
      })
      .catch(() => {
        if (!active) return;
        setStatus('unpublished');
      });
    return () => {
      active = false;
    };
  }, [endpoint]);

  return { manifest, status };
}

/**
 * One non-Windows desktop build, read out of the desktop manifest by its
 * `<os>-<arch>` key. `unpublished` until a release has shipped a bundle for
 * that platform, which older manifests simply do not carry, so a card can never
 * advertise a download that would 404.
 *
 * Windows is not addressed this way on purpose: its flat top-level fields serve
 * the clients that predate `platforms` and describe the portable 7z, so the
 * Windows card takes only the version and date from them.
 */
function desktopBuildOf(
  desktop: ManifestState,
  key: string,
): { build: PlatformBuild | null; status: ManifestStatus } {
  if (desktop.status === 'loading') return { build: null, status: 'loading' };
  const build = desktop.manifest?.platforms?.[key] ?? null;
  return { build, status: build ? 'ready' : 'unpublished' };
}

/** The macOS build. Universal, so either arch key resolves to the same bundle. */
export function macBuildOf(desktop: ManifestState) {
  return desktopBuildOf(desktop, 'macos-aarch64');
}

/**
 * The Linux build. x86_64 only: an arm64 Linux visitor sees `unpublished`,
 * which is honest, rather than being offered an amd64 AppImage that will not
 * run on their machine.
 */
export function linuxBuildOf(desktop: ManifestState) {
  return desktopBuildOf(desktop, 'linux-x86_64');
}

/**
 * Which build to lead with. Resolved after mount so the first paint is
 * identical for every visitor and the platform check can never cause a flash
 * of the wrong primary action mid-render.
 */
export function useVisitorPlatform(): VisitorPlatform {
  const [platform, setPlatform] = useState<VisitorPlatform>('windows');
  useEffect(() => setPlatform(detectVisitorPlatform()), []);
  return platform;
}

/**
 * Size in bytes of a same-origin download, read from a HEAD request's
 * Content-Length. For a build whose manifest does not describe it (the Windows
 * installer), so the button can still show its size. Undefined until known or
 * when the probe fails; an HTML response (the SPA fallback in local dev) is not
 * a build and is ignored.
 */
export function useDownloadSize(url: string): number | undefined {
  const [size, setSize] = useState<number | undefined>(undefined);

  useEffect(() => {
    let active = true;
    fetch(url, { method: 'HEAD' })
      .then(r => {
        if (!active || !r.ok) return;
        if ((r.headers.get('content-type') ?? '').includes('text/html')) return;
        const n = Number(r.headers.get('content-length'));
        if (Number.isFinite(n) && n > 0) setSize(n);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [url]);

  return size;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
